import { Box, Flex, Group, NumberInput, Radio, Stack, Text } from '@mantine/core';
import dayjs from 'dayjs';
import { useState } from 'react';
import { DateInput } from 'src/common/components/date/DateInput';
import { calculateAgeAtDate, calculateDateFromAge } from './utils';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';

export type SelectionMode = 'date' | 'age' | null;

type ByWhenSelectorProps = {
  value: string | null;
  onChange: (date: string | null) => void;
  clientDateOfBirth: string | null;
  onDateOfBirthChange?: (dateOfBirth: string) => void;
  mode?: SelectionMode;
  onModeChange?: (mode: SelectionMode) => void;
};

type StyledRadioProps = {
  value: string;
  label: string;
  onClick: () => void;
};

function StyledRadio({ value, label, onClick }: StyledRadioProps) {
  return (
    <Radio
      value={value}
      label={
        <Text size="md" fw={FONT_WEIGHTS.semibold} c="gray.10">
          {label}
        </Text>
      }
      variant="outline"
      iconColor="brand.6"
      styles={{
        body: { gap: 6 },
      }}
      onClick={onClick}
    />
  );
}

type RadioCardProps = {
  value: string;
  label: string;
  isExpanded: boolean;
  onClick: () => void;
  children?: React.ReactNode;
};

function RadioCard({ value, label, isExpanded, onClick, children }: RadioCardProps) {
  return (
    <Box
      p={16}
      bg="gray.0"
      style={{
        borderRadius: 'var(--mantine-radius-lg)',
        border: '1px solid var(--mantine-color-gray-1)',
      }}
    >
      <StyledRadio value={value} label={label} onClick={onClick} />
      {isExpanded && (
        <Stack gap="xs" mt="md">
          {children}
        </Stack>
      )}
    </Box>
  );
}

export function ByWhenSelector({
  value,
  onChange,
  clientDateOfBirth,
  onDateOfBirthChange,
  mode: controlledMode,
  onModeChange,
}: ByWhenSelectorProps) {
  const [internalMode, setInternalMode] = useState<SelectionMode>(value ? 'date' : null);
  const mode = controlledMode !== undefined ? controlledMode : internalMode;
  const setMode = (newMode: SelectionMode) => {
    if (onModeChange) {
      onModeChange(newMode);
    } else {
      setInternalMode(newMode);
    }
  };

  const correspondingAge =
    value && clientDateOfBirth ? calculateAgeAtDate(clientDateOfBirth, value) : null;

  const handleDateChange = (date: string | null) => {
    if (date) {
      onChange(dayjs(date).format('YYYY-MM-DD'));
    } else {
      onChange(null);
    }
  };

  const handleAgeChange = (age: number | string) => {
    if (typeof age === 'number' && clientDateOfBirth) {
      const targetDate = calculateDateFromAge(clientDateOfBirth, age);
      onChange(targetDate);
    }
  };

  const handleModeChange = (newMode: string) => {
    if (newMode === mode) {
      setMode(null);
      onChange(null);
    } else {
      setMode(newMode as SelectionMode);
    }
  };

  const handleDateOfBirthChange = (date: string | null) => {
    if (date && onDateOfBirthChange) {
      onDateOfBirthChange(dayjs(date).format('YYYY-MM-DD'));
    }
  };

  const currentAge = correspondingAge ?? '';

  return (
    <Stack gap="xs">
      <Text fw={FONT_WEIGHTS.semibold} size="xl" c="gray.10">
        By when
      </Text>
      <Radio.Group value={mode ?? ''} onChange={handleModeChange}>
        <Stack gap={8}>
          <RadioCard
            value="date"
            label="Set as a date"
            isExpanded={mode === 'date'}
            onClick={() => handleModeChange('date')}
          >
            <Group grow>
              <DateInput
                label="Target date"
                value={value}
                onChange={handleDateChange}
                placeholder="DD/MM/YYYY"
                leftSection={null}
                clearable={false}
                styles={{ input: { borderRadius: 'var(--mantine-radius-md)' } }}
              />
              <DateInput
                label="Date of birth"
                value={clientDateOfBirth}
                onChange={handleDateOfBirthChange}
                placeholder="DD/MM/YYYY"
                leftSection={null}
                clearable={false}
                maxDate={new Date()}
                styles={{ input: { borderRadius: 'var(--mantine-radius-md)' } }}
              />
            </Group>
            {correspondingAge !== null && (
              <Flex justify="space-between">
                <Text size="sm" c="gray.6">
                  Corresponding age:
                </Text>
                <Text size="sm">{correspondingAge} years old</Text>
              </Flex>
            )}
          </RadioCard>

          <RadioCard
            value="age"
            label="Set as an age"
            isExpanded={mode === 'age'}
            onClick={() => handleModeChange('age')}
          >
            <Group grow>
              <NumberInput
                label="Target age"
                value={currentAge}
                onChange={handleAgeChange}
                placeholder="Enter target age"
                min={0}
                max={120}
                disabled={!clientDateOfBirth}
                hideControls
                styles={{ input: { borderRadius: 'var(--mantine-radius-md)' } }}
              />
              <DateInput
                label="Date of birth"
                value={clientDateOfBirth}
                onChange={handleDateOfBirthChange}
                placeholder="DD/MM/YYYY"
                leftSection={null}
                clearable={false}
                maxDate={new Date()}
                styles={{ input: { borderRadius: 'var(--mantine-radius-md)' } }}
              />
            </Group>
            {!clientDateOfBirth && (
              <Text size="xs" c="red">
                Client date of birth is required to calculate target date from age
              </Text>
            )}
            {value && clientDateOfBirth && (
              <Flex justify="space-between">
                <Text size="sm" c="gray.6">
                  Corresponding date:
                </Text>
                <Text size="sm">{dayjs(value).format('DD/MM/YYYY')}</Text>
              </Flex>
            )}
          </RadioCard>
        </Stack>
      </Radio.Group>
    </Stack>
  );
}
